import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Calendar, Users, ChefHat, Clock, MoreHorizontal, Share2, XCircle, RotateCcw, DollarSign, MessageCircle, RefreshCw } from "lucide-react";
import { getDocumentLabel, PIPELINE_STAGES, PIPELINE_STAGE_LABELS, type PipelineStage, serviceTypeLabel } from "@/types/quotes";
import type { QuoteRow } from "@/hooks/useQuotes";
import { publicQuoteUrl } from "@/lib/public-urls";
import { formatRelativeTime } from "@/lib/format-relative-time";
import { toast } from "sonner";

const STAGE_BORDER_COLORS: Record<string, string> = {
  new: "border-l-muted-foreground",
  qualifying: "border-l-[hsl(42,78%,50%)]",
  quote_sent: "border-l-primary",
  deposit_pending: "border-l-warning",
  booked: "border-l-success",
  won: "border-l-primary",
};

const PAYMENT_BADGE: Record<string, { label: string; className: string }> = {
  partial: { label: "Partial", className: "bg-warning/20 text-warning border-warning/40" },
  paid: { label: "Paid", className: "bg-success/20 text-success-foreground border-success/40" },
};

interface QuotePipelineCardProps {
  quote: QuoteRow;
  onClick: () => void;
  onContactClick?: (contactId: string) => void;
  onStageChange?: (id: string, stage: string) => void;
  onToggleLost?: (id: string, markLost: boolean) => void;
}

export const QuotePipelineCard = React.forwardRef<HTMLDivElement, QuotePipelineCardProps>(function QuotePipelineCard({ quote, onClick, onContactClick, onStageChange, onToggleLost }, ref) {
  const formatDate = (d: string | null) => {
    if (!d) return null;
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatFullDate = (d: string | null) => {
    if (!d) return null;
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  const total = Number(quote.total);
  const amountPaid = Number(quote.amount_paid ?? 0);
  const balance = Math.max(0, total - amountPaid);
  const hasPayments = amountPaid > 0;
  const paymentStatus = (quote as QuoteRow & { payment_status?: string }).payment_status ?? "unpaid";
  const docLabel = getDocumentLabel(quote.status, paymentStatus);
  const stage = quote.pipeline_stage as PipelineStage;
  const isLost = quote.is_lost;
  const borderColor = isLost ? "border-l-destructive" : (STAGE_BORDER_COLORS[stage] || "border-l-muted-foreground");
  const lastPayment = quote.latest_payment?.[0] ?? null;

  // Overdue check
  const isOverdue = quote.payment_due_date && paymentStatus !== "paid" &&
    new Date(quote.payment_due_date + "T00:00:00") < new Date();

  // Revision-requested badge (only meaningful in 'qualifying' stage).
  const revisionRequestedAt = quote.revision_requested_at ?? null;
  const showRevisionBadge = stage === "qualifying" && !!revisionRequestedAt;
  const revisionAgeLabel = (() => {
    if (!revisionRequestedAt) return "";
    const ms = Date.now() - new Date(revisionRequestedAt).getTime();
    const h = Math.floor(ms / 3_600_000);
    if (h < 1) return "just now";
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  })();

  // Days ago for leads
  const daysAgo = Math.floor((Date.now() - new Date(quote.created_at).getTime()) / 86400000);
  const createdFormatted = new Date(quote.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const lastContactedAt = quote.contact?.last_message_at;
  const stageChangedAt = quote.pipeline_stage_changed_at;
  const lastContactedLabel = formatRelativeTime(lastContactedAt);
  const stageChangedLabel = formatRelativeTime(stageChangedAt);

  const handleShareLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(publicQuoteUrl(quote.id));
    toast.success("Link copied!");
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={ref}
        onClick={onClick}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("text/plain", quote.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        className={`bg-card border rounded-lg p-3 sm:cursor-grab sm:active:cursor-grabbing hover:shadow-md transition-shadow space-y-2 border-l-[3px] ${borderColor} group relative ${isLost ? "opacity-50" : ""}`}
      >
        {/* Quick actions — visible by default (touch has no hover), fades in on hover for mouse users */}
        <div className="absolute top-2 right-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleShareLink}>
                <Share2 className="h-3.5 w-3.5 mr-2" />
                Share Link
              </DropdownMenuItem>
              {onStageChange && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Move to</DropdownMenuLabel>
                  {PIPELINE_STAGES.filter((s) => s !== stage).map((s) => (
                    <DropdownMenuItem key={s} onClick={() => onStageChange(quote.id, s)}>
                      {PIPELINE_STAGE_LABELS[s]}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              {onToggleLost && !isLost && (
                <DropdownMenuItem onClick={() => onToggleLost(quote.id, true)} className="text-destructive focus:text-destructive">
                  <XCircle className="h-3.5 w-3.5 mr-2" />
                  Mark Lost
                </DropdownMenuItem>
              )}
              {onToggleLost && isLost && (
                <DropdownMenuItem onClick={() => onToggleLost(quote.id, false)}>
                  <RotateCcw className="h-3.5 w-3.5 mr-2" />
                  Re-open
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Lost badge */}
        {isLost && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 bg-destructive/10 text-destructive border-destructive/30">
            Lost
          </Badge>
        )}

        {/* Header: client + total, then quote number on its own line so a wide total never squeezes it (#978) */}
        <div>
          <div className="flex items-start justify-between gap-2 pr-8">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">
                {quote.contact_id && onContactClick ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); onContactClick(quote.contact_id!); }}
                    className="block max-w-full hover:text-primary hover:underline transition-colors text-left truncate"
                  >
                    {quote.client_name}
                  </button>
                ) : (
                  quote.client_name
                )}
              </div>
            </div>
            {total > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-sm font-semibold shrink-0 whitespace-nowrap">${total.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  <p>Total: ${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  {hasPayments && <p className="text-success-foreground">Paid: ${amountPaid.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>}
                  {hasPayments && <p>Balance: ${balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-1.5 pr-8">
            <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">{quote.quote_number}</span>
            <Badge variant="outline" className="text-[9px] px-1 py-0 font-normal">
              {docLabel}
            </Badge>
          </div>
        </div>

        {/* Revision requested badge — only on qualifying stage */}
        {showRevisionBadge && (
          <div className="flex items-center gap-1 text-[10px] font-medium text-[hsl(42,78%,30%)] bg-[hsl(42,78%,60%)]/15 border border-[hsl(42,78%,60%)]/40 rounded px-1.5 py-0.5 w-fit">
            <span>Revision requested</span>
            <span className="text-[hsl(42,78%,30%)]/70">· {revisionAgeLabel}</span>
          </div>
        )}

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {quote.event_date ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDate(quote.event_date)}
                </span>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Event date: {formatFullDate(quote.event_date)}</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-[10px] italic">Added {daysAgo === 0 ? "today" : `${daysAgo}d ago`}</span>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Created on {createdFormatted} · No event date set</TooltipContent>
            </Tooltip>
          )}
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {quote.guest_count}
          </span>
          <span className="flex items-center gap-1">
            <ChefHat className="h-3 w-3" />
            {serviceTypeLabel(quote.service_type, quote.fulfillment_type).split(/[\s(]/)[0]}
          </span>
        </div>

        {/* Event type */}
        {quote.event_type?.name && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                  {quote.event_type.name}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">Event type</TooltipContent>
          </Tooltip>
        )}

        {/* Payment due date */}
        {quote.payment_due_date && paymentStatus !== "paid" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={`flex items-center gap-1 text-[10px] ${isOverdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                <Clock className="h-3 w-3" />
                Due {formatDate(quote.payment_due_date)}
                {isOverdue && " — Overdue"}
              </div>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              <p>Payment due: {formatFullDate(quote.payment_due_date)}</p>
              {isOverdue && <p className="text-destructive font-medium">This payment is past due</p>}
              <p>Balance remaining: ${balance.toFixed(2)}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Payment progress + status + last payment date */}
        {hasPayments && (
          <div className="space-y-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5">
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-success rounded-full transition-all"
                      style={{ width: `${Math.min(100, (amountPaid / total) * 100)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">${amountPaid.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                <p>Paid: ${amountPaid.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} of ${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p>Balance: ${balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p>{Math.min(100, Math.round((amountPaid / total) * 100))}% collected</p>
              </TooltipContent>
            </Tooltip>
            <div className="flex items-center gap-1.5 flex-wrap">
              {PAYMENT_BADGE[paymentStatus] && (
                <Badge variant="outline" className={`text-[9px] px-1 py-0 ${PAYMENT_BADGE[paymentStatus].className}`}>
                  {PAYMENT_BADGE[paymentStatus].label}
                </Badge>
              )}
              {lastPayment && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <DollarSign className="h-2.5 w-2.5" />
                      {formatDate(lastPayment.payment_date)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">
                    <p>Last payment: ${Number(lastPayment.amount).toFixed(2)}</p>
                    <p>Date: {formatFullDate(lastPayment.payment_date)}</p>
                    <p>Method: {lastPayment.method}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        {/* Square source indicator */}
        {(quote as QuoteRow & { square_source?: string }).square_source && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-primary/10 text-primary text-[9px] font-bold">S</span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">Imported from Square</TooltipContent>
          </Tooltip>
        )}

        {/* Last contacted & stage changed timestamps (#590) */}
        {(lastContactedLabel || stageChangedLabel) && (
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
            {lastContactedLabel && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-0.5">
                    <MessageCircle className="h-2.5 w-2.5" />
                    {lastContactedLabel}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  <p>Last contacted: {new Date(lastContactedAt!).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {stageChangedLabel && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-0.5">
                    <RefreshCw className="h-2.5 w-2.5" />
                    {stageChangedLabel}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  <p>Stage changed: {new Date(stageChangedAt!).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
});
